import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useRef, useState } from "react";
import axios from "axios";
import { StyledButton } from "../../../common/Buttons";
import { BroadcastAttachment } from "../broadcastTypes";

interface AttachmentUploaderProps {
  attachments: BroadcastAttachment[];
  setAttachments: (attachments: BroadcastAttachment[]) => void;
}

// SendGrid caps a single message (body + all attachments, base64-encoded) at
// 30MB. Keep the raw total comfortably under that.
const MAX_TOTAL_BYTES = 20 * 1024 * 1024;

const AttachmentUploader = ({
  attachments,
  setAttachments,
}: AttachmentUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError("");

    let runningTotal = attachments.reduce(
      (sum, a: any) => sum + (a.size || 0),
      0,
    );
    const next: BroadcastAttachment[] = [...attachments];

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (next.find((a) => a.name === file.name)) continue;
        if (runningTotal + file.size > MAX_TOTAL_BYTES) {
          setError(
            "Attachments exceed the 20MB total limit. Remove a file and try again.",
          );
          break;
        }

        const formData = new FormData();
        formData.append("file", file);
        const result = await axios.post(`/upload/attachment`, formData, {
          baseURL: process.env.REACT_APP_API_DOMAIN,
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });

        runningTotal += file.size;
        next.push({
          name: result.data.attachmentName,
          storageName: result.data.storageName,
          path: `/public/email/upload/` + result.data.storageName,
          download: `${process.env.REACT_APP_API_DOMAIN}/upload/attachment/${result.data.storageName}/${result.data.attachmentName}`,
          // kept client-side only for the running total; harmless if it reaches the server
          size: file.size,
        } as any);
      }
      setAttachments(next);
    } catch (err: any) {
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAttachment = (name: string) => {
    setAttachments(attachments.filter((a) => a.name !== name));
  };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => handleFiles(e.target.files)}
      />
      <Stack direction={"row"} spacing={1} alignItems={"center"}>
        <StyledButton
          variant="outlined"
          startIcon={<AttachFileIcon />}
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? "Uploading…" : "Add Attachment"}
        </StyledButton>
        <Typography variant="body2" color="text.secondary">
          Files ride along with every recipient's email.
        </Typography>
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {attachments.length > 0 && (
        <Stack direction={"row"} spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
          {attachments.map((attachment) => (
            <Chip
              key={attachment.name}
              label={attachment.name}
              onDelete={() => removeAttachment(attachment.name)}
              sx={{ mb: 1 }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default AttachmentUploader;
